import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Auth Provider
import { AuthProvider } from "./hooks/useAuth";

// Layout
import AdminLayout from "./layouts/AdminLayout";

// Pages
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import UserDetail from "./pages/UserDetail";
import Blogs from "./pages/Blogs";
import Faqs from "./pages/Faqs";
import Orders from "./pages/Orders";
import Products from "./pages/Products";
import Settings from "./pages/Settings";
import AdManagement from "./pages/AdManagement";
import BotPerformance from "./pages/BotPerformance";
import Subscriptions from "./pages/Subscriptions";
import Referrals from "./pages/Referrals";
import Login from "./pages/Login";
import ExamDetail from "./pages/ExamDetail";
import BotStrategy from "./pages/BotStrategy";
import Suggestions from "./pages/Suggestions";
import PairsManagement from "./pages/PairsManagement";
import ProxyManagement from "./pages/ProxyManagement";
import { ToastProvider } from "./context/toastContext";
import ArticleManagement from "./pages/ArticleManagement";
import ArticleEditManagement from "./pages/ArticleEditManagement";
import ArticleCategoryManagement from "./pages/CategoryManagement";
import ArticleCreateManagement from "./pages/ArticleCreateManagement";
import EventManagement from "./pages/EventManagement";
import EventEditManagement from "./pages/EventEditManagement";
import EventCreateManagement from "./pages/EventCreateManagement";
import EventDetailManagement from "./pages/EventDetailPage";
// Lazy loaded Course Management components
const CourseManagement = lazy(() => import("./pages/CourseManagement"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const CourseParticipants = lazy(() => import("./pages/CourseParticipants"));

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-full w-full">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

export default function AdminPanel() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Routes>
          {/* Login route (unused in development) */}
          <Route path="/login" element={<Login />} />

          {/* Root path redirect to admin-v2 */}
          <Route path="/" element={<Navigate to="/admin-v2" replace />} />

          {/* Admin v2 routes with prefix */}
          <Route path="/admin-v2" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="users" element={<Users />} />
            <Route path="users/:userId" element={<UserDetail />} />
            <Route path="suggestions" element={<Suggestions />} />
            <Route path="bot-strategy" element={<BotStrategy />} />
            <Route path="blogs" element={<Blogs />} />
            <Route path="faqs" element={<Faqs />} />
            <Route path="orders" element={<Orders />} />
            <Route path="products" element={<Products />} />
            <Route path="settings" element={<Settings />} />
            <Route path="bot-performance" element={<BotPerformance />} />
            <Route path="ad-management" element={<AdManagement />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="pairs-management" element={<PairsManagement />} />
            <Route path="article" element={<ArticleManagement />} />
            <Route path="article/edit/:slug" element={<ArticleEditManagement />} />
            <Route path="article/create" element={<ArticleCreateManagement />} />
            <Route path="article/category" element={<ArticleCategoryManagement />} />
            <Route path="events" element={<EventManagement />} />
            <Route path="events/create" element={<EventCreateManagement />} />
            <Route path="events/edit/:slug" element={<EventEditManagement />} />
            <Route path="events/view/:slug" element={<EventDetailManagement />} />

            <Route
              path="course-management"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CourseManagement />
                </Suspense>
              }
            />
            <Route
              path="course-management/:courseId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CourseDetail />
                </Suspense>
              }
            />
            <Route
              path="course-management/:courseId/participants"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CourseParticipants />
                </Suspense>
              }
            />
            <Route
              path="exam/detail/:examId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamDetail />
                </Suspense>
              }
            />
            <Route path="proxies" element={<ProxyManagement />} />
          </Route>

          {/* Regular admin routes without prefix - fallback */}
          <Route element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="users" element={<Users />} />
            <Route path="users/:userId" element={<UserDetail />} />
            <Route path="suggestions" element={<Suggestions />} />
            <Route path="bot-strategy" element={<BotStrategy />} />
            <Route path="blogs" element={<Blogs />} />
            <Route path="faqs" element={<Faqs />} />
            <Route path="orders" element={<Orders />} />
            <Route path="products" element={<Products />} />
            <Route path="settings" element={<Settings />} />
            <Route path="bot-performance" element={<BotPerformance />} />
            <Route path="ad-management" element={<AdManagement />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="article" element={<ArticleManagement />} />
            <Route path="article/edit/:slug" element={<ArticleEditManagement />} />
            <Route path="article/create" element={<ArticleCreateManagement />} />
            <Route path="article/category" element={<ArticleCategoryManagement />} />
            <Route path="events" element={<EventManagement />} />
            <Route path="events/edit/:slug" element={<EventEditManagement />} />
            <Route path="events/view/:slug" element={<EventDetailManagement />} />
            <Route path="events/create" element={<EventCreateManagement />} />

            <Route
              path="course-management"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CourseManagement />
                </Suspense>
              }
            />
            <Route
              path="course-management/:courseId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CourseDetail />
                </Suspense>
              }
            />
            <Route
              path="course-management/:courseId/participants"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <CourseParticipants />
                </Suspense>
              }
            />
            <Route
              path="exam/detail/:examId"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ExamDetail />
                </Suspense>
              }
            />
          </Route>
          <Route path="proxies" element={<ProxyManagement />} />
        </Routes>
      </ToastProvider>
    </AuthProvider>
  );
}
